import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { dataService } from '../../services/dataService';
import { confirmDialog } from '../../utils/dialog';

const CHAT_PREFIX = 'projectly_chat_';

function parseChatKey(chatKey) {
  const m = chatKey.match(/^(.+?)_(agent|team|direct)_(.*)$/);
  if (!m) return { nodeId: chatKey, mode: 'unknown', agentName: '' };
  return { nodeId: m[1], mode: m[2], agentName: m[3] };
}

function loadAllChats() {
  const chats = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k?.startsWith(CHAT_PREFIX)) continue;
    const chatKey = k.slice(CHAT_PREFIX.length);
    try {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const data = JSON.parse(raw);
      let sessions;
      if (Array.isArray(data)) {
        sessions = [{ id: 'cs_migrated', title: 'Previous chat', messages: data, createdAt: null }];
      } else if (data?.sessions?.length) {
        sessions = data.sessions;
      } else {
        continue;
      }
      chats.push({ storageKey: k, chatKey, sessions });
    } catch {}
  }
  // Most recently updated first
  chats.sort((a, b) => {
    const aLast = a.sessions[a.sessions.length - 1]?.createdAt || '';
    const bLast = b.sessions[b.sessions.length - 1]?.createdAt || '';
    return bLast.localeCompare(aLast);
  });
  return chats;
}

function lastMessage(session) {
  const msgs = session.messages || [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].role === 'user') return msgs[i].text || msgs[i].content || '';
    if (msgs[i].role === 'assistant' && i > 0) return msgs[i].text || '';
  }
  return '';
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export default function ChatsPage({ workspaceId, onNavigate, opsMode }) {
  const [chats, setChats] = useState([]);
  const [nodeMap, setNodeMap] = useState({});
  const [expandedChat, setExpandedChat] = useState(null);

  const refreshChats = useCallback(() => setChats(loadAllChats()), []);

  useEffect(() => {
    const local = loadAllChats();
    if (local.length > 0) {
      setChats(local);
    } else if (dataService.userId) {
      // localStorage is empty (incognito or cleared) — load listing from Firestore
      getDocs(collection(db, 'users', dataService.userId, 'chats'))
        .then(snap => {
          const fsChats = snap.docs
            .map(d => {
              const data = d.data();
              // d.id is the storageKey (with prefix), chatKey is without prefix
              const chatKey = d.id.startsWith(CHAT_PREFIX) ? d.id.slice(CHAT_PREFIX.length) : d.id;
              return { storageKey: d.id, chatKey, sessions: data.sessions || [] };
            })
            .filter(c => c.sessions.length > 0);
          if (fsChats.length > 0) setChats(fsChats);
        })
        .catch(() => {});
    }
    const onStorage = (e) => { if (e.key?.startsWith(CHAT_PREFIX)) refreshChats(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshChats]);

  useEffect(() => {
    if (!workspaceId) return;
    dataService.getPipeline(workspaceId).then(data => {
      const m = {};
      (data.nodes || []).forEach(n => { m[n.id] = n.data?.label || n.id; });
      setNodeMap(m);
    });
  }, [workspaceId]);

  const openChat = useCallback((chatKey) => {
    localStorage.setItem('projectly_open_chat', chatKey);
    onNavigate(opsMode ? 'operations' : 'pipeline');
  }, [onNavigate, opsMode]);

  const deleteChat = useCallback(async (storageKey, chatKey) => {
    if (!(await confirmDialog({ title: 'Delete chat history', message: 'Delete all chat history for this agent?', confirmText: 'Delete', danger: true }))) return;
    localStorage.removeItem(storageKey);
    setChats(prev => prev.filter(c => c.storageKey !== storageKey));
    if (expandedChat === chatKey) setExpandedChat(null);
  }, [expandedChat]);

  const deleteSession = useCallback((storageKey, chatKey, sessionId) => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      const sessions = (data.sessions || []).filter(s => s.id !== sessionId);
      if (sessions.length === 0) {
        localStorage.removeItem(storageKey);
        setChats(prev => prev.filter(c => c.storageKey !== storageKey));
        if (expandedChat === chatKey) setExpandedChat(null);
      } else {
        const updated = { ...data, sessions, currentId: sessions[sessions.length - 1].id };
        localStorage.setItem(storageKey, JSON.stringify(updated));
        setChats(prev => prev.map(c => c.storageKey === storageKey ? { ...c, sessions } : c));
      }
    } catch {}
  }, [expandedChat]);

  const getChatTitle = (chatKey) => {
    const { nodeId, mode, agentName } = parseChatKey(chatKey);
    const nodeName = nodeMap[nodeId] || nodeId;
    if (mode === 'agent' && agentName) return agentName;
    if (mode === 'team') return `${nodeName} team`;
    if (mode === 'direct') return nodeName;
    return chatKey;
  };

  const getChatSubtitle = (chatKey) => {
    const { nodeId, mode, agentName } = parseChatKey(chatKey);
    const nodeName = nodeMap[nodeId] || nodeId;
    if (mode === 'agent' && agentName) return `${nodeName} · Agent`;
    if (mode === 'team') return `${nodeName} · Team chat`;
    if (mode === 'direct') return `${nodeName} · Direct message`;
    return nodeName;
  };

  if (chats.length === 0) {
    return (
      <div className="chats-page-empty">
        <div className="chats-empty-icon">💬</div>
        <p>No chat history yet.</p>
        <p className="chats-empty-sub">Start a chat with an agent from the {opsMode ? 'Operations' : 'Pipeline'} page.</p>
        <button className="agents-go-btn" onClick={() => onNavigate(opsMode ? 'operations' : 'pipeline')}>
          Go to {opsMode ? 'Operations' : 'Pipeline'}
        </button>
      </div>
    );
  }

  return (
    <div className="chats-page">
      <div className="chats-page-header">
        <h1 className="chats-page-title">Chats</h1>
        <div className="chats-page-sub">
          {chats.length} conversation{chats.length !== 1 ? 's' : ''}
          {' · '}
          {chats.reduce((s, c) => s + c.sessions.length, 0)} session{chats.reduce((s, c) => s + c.sessions.length, 0) !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="chats-list">
        {chats.map(chat => {
          const isExpanded = expandedChat === chat.chatKey;
          const lastSession = chat.sessions[chat.sessions.length - 1];
          const preview = lastMessage(lastSession);

          return (
            <div key={chat.chatKey} className={`chat-card${isExpanded ? ' expanded' : ''}`}>
              <div className="chat-card-header" onClick={() => setExpandedChat(isExpanded ? null : chat.chatKey)}>
                <div className="chat-card-left">
                  <div className="chat-card-avatar">💬</div>
                  <div className="chat-card-info">
                    <div className="chat-card-name">{getChatTitle(chat.chatKey)}</div>
                    <div className="chat-card-subtitle">{getChatSubtitle(chat.chatKey)}</div>
                    {!isExpanded && preview && (
                      <div className="chat-card-preview">{preview.slice(0, 80)}{preview.length > 80 ? '…' : ''}</div>
                    )}
                  </div>
                </div>
                <div className="chat-card-meta">
                  <span className="chat-sessions-count">{chat.sessions.length} session{chat.sessions.length !== 1 ? 's' : ''}</span>
                  <span className="chat-last-date">{fmtDate(lastSession?.createdAt)}</span>
                  <div className="chat-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="chat-open-btn" onClick={() => openChat(chat.chatKey)} title="Open on pipeline">
                      ↗ Open
                    </button>
                    <button className="chat-delete-btn" onClick={() => deleteChat(chat.storageKey, chat.chatKey)} title="Delete all history">
                      ×
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="chat-sessions-list">
                  {[...chat.sessions].reverse().map(sess => {
                    const msgCount = (sess.messages || []).filter(m => m.role === 'user').length;
                    const preview = lastMessage(sess);
                    return (
                      <div key={sess.id} className="chat-session-item">
                        <div className="chat-session-main">
                          <div className="chat-session-title">{sess.title || 'Chat'}</div>
                          <div className="chat-session-meta">
                            {msgCount} message{msgCount !== 1 ? 's' : ''}
                            {sess.createdAt && ` · ${fmtDate(sess.createdAt)}`}
                          </div>
                          {preview && (
                            <div className="chat-session-preview">{preview.slice(0, 100)}{preview.length > 100 ? '…' : ''}</div>
                          )}
                        </div>
                        <button
                          className="chat-session-del"
                          onClick={() => deleteSession(chat.storageKey, chat.chatKey, sess.id)}
                          title="Delete this session"
                        >×</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
